import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { formatDate } from '../../services/utils/date';
import authService from '../../services/api/auth';
import { useToast } from '../../components/common/Toast';
import { useMutation } from 'react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useConfirm } from '../../hooks/useConfirm';
import DarkModeToggle from '../../components/common/DarkModeToggle';
import { 
  Lock, 
  Bell, 
  Shield, 
  Eye, 
  EyeOff, 
  Save, 
  Trash2,
  CreditCard,
  Settings as SettingsIcon,
  Globe,
  AlertTriangle,
  Mail,
  ShieldCheck,
  History,
  Smartphone,
  CheckCircle2,
  Calendar,
  Clock,
  FileText,
  MessageCircle,
  Zap
} from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const toast = useToast();

  // Dark mode state
  const [isDarkMode] = useState(() => {
    // Check localStorage and system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply dark mode on component mount
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, [isDarkMode]);



  // نموذج تغيير كلمة المرور
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
  } = useForm();

  // نموذج تفضيلات الإشعارات
  const {
    register: registerNotifications,
    handleSubmit: handleNotificationsSubmit,
  } = useForm({
    defaultValues: {
      email_notifications: true,
      push_notifications: true,
      course_updates: true,
      new_questions: true,
      subscription_reminders: true,
    }
  });

  // Mutation تغيير كلمة المرور
  const passwordMutation = useMutation(
    async (passwordData) => {
      const response = await authService.changePassword(passwordData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم تحديث كلمة المرور بنجاح حماية حسابك الآن أقوى');
        resetPassword();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'عذراً، فشل تحديث كلمة المرور');
      }
    }
  );

  // Mutation تفضيلات الإشعارات
  const notificationsMutation = useMutation(
    async (preferences) => {
      const response = await authService.updateNotificationPreferences(preferences);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم حفظ تفضيلات التنبيهات بنجاح');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'حدث خطأ أثناء حفظ التفضيلات');
      }
    }
  );

  // Mutation حذف الحساب
  const deleteAccountMutation = useMutation(
    async () => {
      const response = await authService.deleteAccount();
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم إغلاق الحساب نهائياً، نأمل رؤيتك مجدداً');
        window.location.href = '/login';
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل حذف الحساب');
      }
    }
  );

  const onPasswordSubmit = (data) => {
    if (data.new_password !== data.confirm_password) {
      toast.error('تأكيد كلمة المرور الجديدة غير متطابق');
      return;
    }
    passwordMutation.mutate({
      currentPassword: data.current_password,
      newPassword: data.new_password,
    });
  };

  const onNotificationsSubmit = (data) => {
    notificationsMutation.mutate(data);
  };

  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      title: 'حذف الحساب نهائياً',
      message: 'هل أنت متأكد من رغبتك في حذف حسابك؟ لن تتمكن من استعادة بياناتك أو اشتراكاتك بعد هذه الخطوة.',
      type: 'danger'
    });
    
    if (confirmed) {
      const finalConfirmed = await confirm({
        title: 'التحذير الأخير والمؤكد',
        message: 'سيتم مسح كافة سجلات التعلم والمدفوعات الخاصة بك. هل أنت واثق تماماً؟',
        type: 'danger'
      });
      
      if (finalConfirmed) {
        deleteAccountMutation.mutate();
      }
    }
  };

  const tabs = [
    { id: 'general', name: 'عامة', icon: SettingsIcon, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'security', name: 'الأمان', icon: Shield, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { id: 'notifications', name: 'الإشعارات', icon: Bell, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: 'billing', name: 'المدفوعات', icon: CreditCard, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-8 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <div className="bg-primary-600 text-white p-1.5 rounded-xl"><SettingsIcon size={24} /></div>
                تخصيص الإعدادات
            </h1>
            <p className="text-gray-500 font-medium">تحكم في خصوصيتك، أمان حسابك، وكيفية تواصلنا معك</p>
        </div>
        <div className="bg-white px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
            <Calendar size={14} className="text-primary-500" />
            آخر تحديث: {formatDate(new Date())}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Navigation Tabs */}
        <div className="lg:col-span-3">
            <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-gray-100 space-y-2 sticky top-24">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                            w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-black text-sm transition-all duration-300
                            ${activeTab === tab.id
                                ? `${tab.bg} ${tab.color} shadow-lg shadow-gray-100 translate-x-[-4px]`
                                : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                            }
                        `}
                    >
                        <tab.icon size={20} />
                        {tab.name}
                    </button>
                ))}
            </div>
        </div>

        {/* Right Column: Tab Content */}
        <div className="lg:col-span-9">
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden min-h-[600px] animate-in fade-in duration-500">
                
                <div className="p-8 lg:p-12">
                {/* General Settings Tab */}
                {activeTab === 'general' && (
                    <div className="space-y-10">
                        <div className="space-y-1">
                            <h2 className="text-2xl font-black text-gray-900">الإعدادات العامة</h2>
                            <p className="text-gray-500 text-sm font-medium">اختر لغتك المفضلة وتنسيق الوقت المناسب لمنطقتك</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                                    <Globe size={14} className="text-primary-600" /> لغة واجهة المنصة
                                </label>
                                <select className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer">
                                    <option value="ar">العربية (اللغة الافتراضية)</option>
                                    <option value="en">English (Coming Soon)</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                                    <Clock size={14} className="text-primary-600" /> المنطقة الزمنية
                                </label>
                                <select className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer">
                                    <option value="Asia/Cairo">القاهرة (GMT+2)</option>
                                    <option value="Asia/Riyadh">الرياض (GMT+3)</option>
                                    <option value="Asia/Dubai">دبي (GMT+4)</option>
                                </select>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-50">
                            <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 flex items-center justify-between group hover:bg-white hover:shadow-md transition-all">
                                <div className="flex items-center gap-4">
                                    <div className="text-base font-black text-gray-900">الوضع الليلي (Dark Mode)</div>
                                    <div className="text-xs font-medium text-gray-400">تغيير مظهر المنصة ليكون مريحاً للعين في الإضاءة الخافتة</div>
                                </div>
                                <DarkModeToggle 
                                    size="default"
                                    showLabel={false}
                                    className="ml-4"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Security Settings Tab */}
                {activeTab === 'security' && (
                    <div className="space-y-12">
                        <div className="space-y-1">
                            <h2 className="text-2xl font-black text-gray-900">مركز الأمان</h2>
                            <p className="text-gray-500 text-sm font-medium">قم بتحديث كلمة مرورك وإدارة ميزات حماية الحساب</p>
                        </div>

                        {/* Change Password Form */}
                        <div className="bg-gray-50/50 rounded-[2rem] p-8 border border-gray-100">
                            <h3 className="text-lg font-black text-gray-900 mb-8 flex items-center gap-2">
                                <Lock size={20} className="text-primary-600" /> تحديث كلمة السر
                            </h3>
                            
                            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2 md:col-span-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">كلمة المرور الحالية</label>
                                        <div className="relative">
                                            <input
                                                {...registerPassword('current_password', { required: 'كلمة المرور الحالية مطلوبة' })}
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                className="w-full px-5 py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold text-gray-900"
                                            />
                                            <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-primary-600">
                                                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">كلمة المرور الجديدة</label>
                                        <div className="relative">
                                            <input
                                                {...registerPassword('new_password', { required: 'الجديدة مطلوبة', minLength: { value: 6, message: '6 أحرف على الأقل' } })}
                                                type={showNewPassword ? 'text' : 'password'}
                                                autoComplete="new-password"
                                                className="w-full px-5 py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold text-gray-900"
                                            />
                                            <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-primary-600">
                                                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">تأكيد كلمة المرور</label>
                                        <div className="relative">
                                            <input
                                                {...registerPassword('confirm_password', { required: 'يرجى التأكيد' })}
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                autoComplete="new-password"
                                                className="w-full px-5 py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold text-gray-900"
                                            />
                                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-primary-600">
                                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <button
                                        type="submit"
                                        disabled={passwordMutation.isLoading}
                                        className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center gap-2"
                                    >
                                        {passwordMutation.isLoading ? <LoadingSpinner size="small" /> : <Save size={20} />}
                                        حفظ كلمة المرور
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Two-Factor Authentication */}
                        <div className="p-8 rounded-[2rem] border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6 group hover:bg-gray-50/50 transition-all">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Smartphone size={28} />
                                </div>
                                <div className="text-center md:text-right">
                                    <p className="text-base font-black text-gray-900">المصادقة الثنائية (2FA)</p>
                                    <p className="text-xs font-medium text-gray-400">إضافة طبقة حماية إضافية عبر تطبيق الهاتف</p>
                                </div>
                            </div>
                            <button className="bg-gray-100 text-gray-500 px-8 py-3 rounded-xl font-bold text-sm hover:bg-primary-600 hover:text-white transition-all transform active:scale-95">تفعيل الآن</button>
                        </div>

                        {/* Account Danger Zone */}
                        <div className="relative overflow-hidden bg-red-50/50 rounded-[2.5rem] p-8 lg:p-10 border border-red-100 group">
                            <div className="absolute top-0 left-0 w-2 h-full bg-red-500 opacity-20 group-hover:opacity-100 transition-opacity"></div>
                            <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
                                <div className="space-y-2 text-center md:text-right">
                                    <div className="flex items-center justify-center md:justify-start gap-2 text-red-600 mb-2">
                                        <AlertTriangle size={24} />
                                        <h3 className="text-xl font-black">إغلاق الحساب نهائياً</h3>
                                    </div>
                                    <p className="text-sm font-medium text-red-700/70 max-w-md">بمجرد حذف حسابك، سيتم مسح كافة اشتراكاتك وتقدمك الدراسي بشكل دائم ولا يمكن التراجع عن هذا الإجراء.</p>
                                </div>
                                <button
                                    onClick={handleDeleteAccount}
                                    disabled={deleteAccountMutation.isLoading}
                                    className="bg-red-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-red-100 hover:bg-red-700 transition-all transform active:scale-95 flex items-center gap-2"
                                >
                                    {deleteAccountMutation.isLoading ? <LoadingSpinner size="small" /> : <Trash2 size={20} />}
                                    حذف حسابي
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notifications Tab */}
                {activeTab === 'notifications' && (
                    <div className="space-y-10">
                        <div className="space-y-1">
                            <h2 className="text-2xl font-black text-gray-900">تفضيلات الإشعارات</h2>
                            <p className="text-gray-500 text-sm font-medium">تحكم في كيفية استلام التنبيهات حول دروسك والنشاطات الجديدة</p>
                        </div>

                        <form onSubmit={handleNotificationsSubmit(onNotificationsSubmit)} className="space-y-6">
                            <div className="divide-y divide-gray-50 bg-gray-50/30 rounded-[2rem] border border-gray-100 px-8">
                                {[
                                    { id: 'email_notifications', label: 'تنبيهات البريد الإلكتروني', sub: 'استلام التقارير والرسائل الهامة عبر بريدك', icon: Mail, color: 'text-blue-500' },
                                    { id: 'push_notifications', label: 'إشعارات المتصفح', sub: 'ظهور تنبيهات فورية أثناء استخدام المنصة', icon: Zap, color: 'text-amber-500' },
                                    { id: 'course_updates', label: 'تحديثات الكورسات', sub: 'إشعار عند رفع دروس أو ملازم جديدة', icon: FileText, color: 'text-emerald-500' },
                                    { id: 'new_questions', label: 'الأسئلة والمناقشات', sub: 'تنبيه عند الرد على أسئلتك أو طرح أسئلة جديدة', icon: MessageCircle, color: 'text-purple-500' },
                                    { id: 'subscription_reminders', label: 'تذكيرات الاشتراك', sub: 'إرسال تنبيه قبل انتهاء صلاحية اشتراكك بـ 3 أيام', icon: ShieldCheck, color: 'text-rose-500' },
                                ].map((item) => (
                                    <div key={item.id} className="py-6 flex items-center justify-between group">
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-xl bg-white shadow-sm ${item.color} group-hover:scale-110 transition-transform`}>
                                                <item.icon size={20} />
                                            </div>
                                            <div>
                                                <p className="text-base font-black text-gray-900">{item.label}</p>
                                                <p className="text-xs font-medium text-gray-400">{item.sub}</p>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input {...registerNotifications(item.id)} type="checkbox" className="sr-only peer" />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-end pt-4">
                                <button
                                    type="submit"
                                    disabled={notificationsMutation.isLoading}
                                    className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center gap-2"
                                >
                                    {notificationsMutation.isLoading ? <LoadingSpinner size="small" /> : <Save size={20} />}
                                    حفظ التفضيلات
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Billing Tab */}
                {activeTab === 'billing' && (
                    <div className="space-y-10">
                        <div className="space-y-1">
                            <h2 className="text-2xl font-black text-gray-900">إعدادات المدفوعات</h2>
                            <p className="text-gray-500 text-sm font-medium">إدارة وسائل الدفع الخاصة بك ومراجعة سجل الفواتير</p>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-gray-50/50 rounded-[2rem] p-8 border border-gray-100">
                                <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2 tracking-tighter uppercase">
                                    <CreditCard size={18} className="text-primary-600" /> المحفظة ووسائل الدفع
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white rounded-2xl border border-gray-100 group hover:border-emerald-200 transition-all shadow-sm">
                                        <div className="flex items-center gap-4 mb-4 sm:mb-0">
                                            <div className="w-12 h-12 bg-gray-900 text-white rounded-xl flex items-center justify-center">
                                                <CreditCard size={24} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-black text-gray-900">بطاقة مدى / ائتمان</p>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase">ينتهي بـ **** 1234</p>
                                            </div>
                                        </div>
                                        <span className="bg-emerald-50 text-emerald-700 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100 flex items-center gap-1.5 self-start">
                                            <CheckCircle2 size={12} /> وسيلة أساسية
                                        </span>
                                    </div>

                                    <button className="w-full py-5 border-4 border-dashed border-gray-100 rounded-[2rem] text-gray-400 font-black text-sm hover:border-primary-200 hover:bg-primary-50/30 hover:text-primary-600 transition-all group flex items-center justify-center gap-2">
                                        <PlusCircle size={20} className="group-hover:scale-110 transition-transform" /> 
                                        إضافة وسيلة دفع جديدة
                                    </button>
                                </div>
                            </div>

                            <div className="bg-white rounded-[2rem] p-8 border border-gray-100">
                                <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2">
                                    <History size={18} className="text-primary-600" /> سجل المعاملات المالية
                                </h3>
                                <div className="py-20 text-center bg-gray-50/50 rounded-[2rem] border border-dashed border-gray-200">
                                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                                        <CreditCard size={32} className="text-gray-200" />
                                    </div>
                                    <h4 className="text-base font-black text-gray-900">لا توجد عمليات سابقة</h4>
                                    <p className="text-gray-400 text-xs font-medium mt-2">ستظهر هنا تفاصيل كافة الكورسات التي قمت بشرائها.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
      </div>
      
      {/* Dynamic Confirmation Dialog Integration */}
      <ConfirmDialogComponent />
    </div>
  );
};

// أيقونات إضافية مستخدمة في التصميم
const PlusCircle = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
);

export default Settings;